import axios from "axios";
import {
  Inventory,
  InventoryFilters,
  InventoryListResponse,
  InventoryCreateData,
  InventoryUpdateData,
  InventoryUploadResponse,
  InventoryOwnershipResponse,
  AdminInventoryFilters,
  AdminInventoryListResponse,
  InventoryCategory,
  InventoryCondition,
} from "../interfaces/Inventory";
import { CATEGORY_OPTIONS } from "../constants/inventoryOptions";
import { apiUrl } from "../config/environment";
import { getAuthHeaders, getToken } from "../utils/authUtils";

const api: string = `${apiUrl}/api/inventory`;

// GET /api/inventory - List inventory items with filters and pagination
export function getInventoryItems(
  filters?: InventoryFilters,
): Promise<{ data: InventoryListResponse }> {
  const params = new URLSearchParams();

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, value.toString());
      }
    });
  }

  const url = params.toString() ? `${api}?${params.toString()}` : api;

  return axios.get(url, getAuthHeaders());
}

// GET /api/inventory/global-search - Global search across all users' inventory
export function getGlobalInventoryItems(
  filters?: {
    q?: string;
    page?: number;
    limit?: number;
    category?: InventoryCategory;
    condition?: InventoryCondition;
    country?: string;
    manufacturer?: string;
    minQty?: number;
    maxQty?: number;
    sort?: "newest" | "oldest" | "price-asc" | "price-desc";
  },
): Promise<{ data: InventoryListResponse }> {
  const params = new URLSearchParams();

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, value.toString());
      }
    });
  }

  const url = params.toString()
    ? `${api}/global-search?${params.toString()}`
    : `${api}/global-search`;

  return axios.get(url, getAuthHeaders()).then((response) => {
    // Map backend response format to InventoryListResponse format
    const backendData = response.data.data;
    return {
      ...response,
      data: {
        items: backendData.items,
        total: backendData.pagination.totalItems,
        page: backendData.pagination.page,
        limit: backendData.pagination.limit,
        pages: backendData.pagination.totalPages,
      },
    };
  });
}

// [2026-07-05] GUEST_ACCESS_PHASE1: anonymous inventory search (no auth).
// Same param + response mapping as getGlobalInventoryItems, but hits the masked
// /api/public route (price/seller stripped server-side per BR-12).
export function getPublicInventorySearch(
  filters?: {
    q?: string;
    page?: number;
    limit?: number;
    category?: InventoryCategory;
    condition?: InventoryCondition;
    country?: string;
    manufacturer?: string;
    minQty?: number;
    maxQty?: number;
    sort?: "newest" | "oldest" | "price-asc" | "price-desc";
  },
): Promise<{ data: InventoryListResponse }> {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, value.toString());
      }
    });
  }
  const publicApi = `${apiUrl}/api/public/inventory/search`;
  const url = params.toString() ? `${publicApi}?${params.toString()}` : publicApi;

  // No auth headers — anonymous endpoint.
  return axios.get(url).then((response) => {
    const backendData = response.data.data;
    return {
      ...response,
      data: {
        items: backendData.items,
        total: backendData.pagination.totalItems,
        page: backendData.pagination.page,
        limit: backendData.pagination.limit,
        pages: backendData.pagination.totalPages,
        // [2026-07-06] GUEST_ACCESS_PHASE2: soft-wall signal (BR-13)
        signupRequired: !!response.data.signupRequired,
        signupMessage: response.data.message || "",
      } as InventoryListResponse & { signupRequired?: boolean; signupMessage?: string },
    };
  });
}

// GET /api/inventory/:id - Get single inventory item
export function getInventoryItem(id: string): Promise<{ data: Inventory }> {
  return axios.get(`${api}/${id}`, getAuthHeaders());
}

// POST /api/inventory - Create new inventory item
export function createInventoryItem(item: InventoryCreateData): Promise<{ data: Inventory }> {
  return axios.post(api, item, getAuthHeaders());
}

// PUT /api/inventory/:id - Update existing inventory item
export function updateInventoryItem(
  id: string,
  item: InventoryUpdateData,
): Promise<{ data: Inventory }> {
  return axios.put(`${api}/${id}`, item, getAuthHeaders());
}

// DELETE /api/inventory/:id - Soft delete inventory item (set isActive=false)
export function deleteInventoryItem(id: string): Promise<{ data: { message: string } }> {
  return axios.delete(`${api}/${id}`, getAuthHeaders());
}

// POST /api/inventory/upload - Bulk upload from CSV/XLSX file (replace mode)
export function uploadInventoryFile(file: File): Promise<{ data: InventoryUploadResponse }> {
  const formData = new FormData();
  formData.append("file", file);

  // [2026-07-08] cookie-auth-fix: cookie (withCredentials) authenticates; no localStorage token required
  const token = getToken();
  return axios.post(`${api}/upload`, formData, {
    headers: {
      Authorization: token || "",
      "Content-Type": "multipart/form-data",
    },
  });
}

// POST /api/inventory/admin/upload-for-user/:userId - Admin upload inventory for a specific user
export async function adminUploadInventoryForUser(
  userId: string,
  file: File,
): Promise<{ data: InventoryUploadResponse }> {
  const formData = new FormData();
  formData.append("file", file);

  // [2026-07-08] cookie-auth-fix: cookie (withCredentials) authenticates; no localStorage token required
  const token = getToken();
  return axios.post(`${api}/admin/upload-for-user/${userId}`, formData, {
    headers: {
      Authorization: token || "",
      "Content-Type": "multipart/form-data",
    },
  });
}

// GET /api/inventory/:id/verify-ownership - Verify if user owns an inventory item
export function verifyInventoryOwnership(
  id: string,
): Promise<{ data: InventoryOwnershipResponse }> {
  return axios.get(`${api}/${id}/verify-ownership`, getAuthHeaders());
}

// GET /api/inventory/admin/by-user/:userId - Admin query inventory by user ID
export function getInventoryByUser(
  userId: string,
  filters?: AdminInventoryFilters,
): Promise<{ data: AdminInventoryListResponse }> {
  const params = new URLSearchParams();

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, value.toString());
      }
    });
  }

  const url = params.toString()
    ? `${api}/admin/by-user/${userId}?${params.toString()}`
    : `${api}/admin/by-user/${userId}`;

  return axios.get(url, getAuthHeaders());
}

// Helper function to validate inventory item data
export function validateInventoryItem(item: Partial<InventoryCreateData>): string[] {
  const errors: string[] = [];

  if (!item.manufacturer || item.manufacturer.trim().length < 2) {
    errors.push("Manufacturer is required and must be at least 2 characters");
  }

  if (!item.partNumber && !item.model) {
    errors.push("Either part number or model must be provided");
  }

  if (!item.condition) {
    errors.push("Condition is required");
  }

  if (item.quantity !== undefined && item.quantity < 1) {
    errors.push("Quantity must be at least 1");
  }

  if (item.price !== undefined && item.price < 0) {
    errors.push("Price cannot be negative");
  }

  if (item.currency && !["USD", "EUR", "GBP"].includes(item.currency)) {
    errors.push("Currency must be USD, EUR, or GBP");
  }

  const validConditions = [
    "NEW Retail",
    "New Bulk",
    "Refurbished",
    "Used",
    "AS-IS",
  ];

  if (item.condition && !validConditions.includes(item.condition)) {
    errors.push(`Condition must be one of: ${validConditions.join(", ")}`);
  }

  if (item.category && !CATEGORY_OPTIONS.includes(item.category as any)) {
    errors.push(`Category must be one of: ${CATEGORY_OPTIONS.join(", ")}`);
  }

  return errors;
}

// Helper function to format inventory item for display
export function formatInventoryItem(item: Inventory): {
  displayTitle: string;
  displayPrice: string;
  displayCondition: string;
  displayCategory: string;
} {
  const displayTitle =
    item.title || `${item.manufacturer} ${item.partNumber || item.model || ""}`.trim();

  const price = item.convertedPrice !== undefined ? item.convertedPrice : item.price;
  const currency = item.displayCurrency || item.currency;
  const displayPrice =
    price !== undefined && price !== null ? `${currency} ${Math.round(price)}` : "Price not set";

  const displayCondition = item.condition || "Unknown";
  const displayCategory = item.category || "Other";

  return {
    displayTitle,
    displayPrice,
    displayCondition,
    displayCategory,
  };
}

// Helper function to generate CSV template
export function generateInventoryCSVTemplate(): string {
  const headers = [
    "Manufacturer",
    "Part Number",
    "Model",
    "Title",
    "Quantity",
    "Condition",
    "Price",
    "Category",
    "Description",
  ];

  const sampleRow = [
    "DJI",
    "DJI-001",
    "Mavic 3",
    "DJI Mavic 3 Pro",
    "5",
    "NEW Retail",
    "1299.99",
    "Drones/UAV",
    "Professional drone with 4K camera",
  ];

  return [headers.join(","), sampleRow.join(",")].join("\n");
}

// Helper function to validate uploaded file
export function validateInventoryFile(file: File): string[] {
  const errors: string[] = [];

  if (!file) {
    errors.push("No file selected");
    return errors;
  }

  const validTypes = [
    "text/csv",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ];

  if (!validTypes.includes(file.type)) {
    errors.push("File must be a CSV or Excel file");
  }

  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    errors.push("File size must be less than 10MB");
  }

  return errors;
}

// [2026-02-13] CURRENCY_PHASE2: Get count of user's active inventory
export function getMyInventoryCount(): Promise<{ data: { count: number } }> {
  return axios.get(`${api}/my-count`, getAuthHeaders());
}

// [2026-02-13] CURRENCY_PHASE2: Delete all user's active inventory (currency change)
export function deleteAllMyInventory(): Promise<{ data: { deleted: number } }> {
  return axios.delete(`${api}/my-all`, getAuthHeaders());
}

// [2026-02-07] BR-16: Confirm inventory is still valid
export function confirmInventory(): Promise<{ data: { message: string; confirmedAt: string } }> {
  return axios.post(`${api}/confirm`, {}, getAuthHeaders());
}

// [2026-02-07] BR-17: Request inventory export
export function requestExport(): Promise<{ data: { message: string; jobId: string } }> {
  return axios.get(`${api}/export`, getAuthHeaders());
}

// [2026-02-07] BR-17: Check export job status
export function getExportStatus(
  jobId: string,
): Promise<{ data: { status: string; itemCount: number; error?: string } }> {
  return axios.get(`${api}/export/${jobId}/status`, getAuthHeaders());
}

// [2026-02-07] BR-17: Download export file
export function downloadExport(jobId: string): Promise<Blob> {
  return axios
    .get(`${api}/export/${jobId}/download`, { ...getAuthHeaders(), responseType: "blob" })
    .then((res) => res.data);
}
// [2026-07-08] redeploy: re-commit cookie-auth fix to trigger rebuild
